// Common
import { Component, inject, Input } from '@angular/core';
import { type HttpContext } from '@angular/common/http';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { mailboxTenantContext, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';

// Services
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';

// Types
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { DragData, dragDataTypeAllowed, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/notes/types/cross-workspace-drop-text';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { VirtualFolder } from '@modules/notes/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { Team } from '@modules/settings/types/team';
import { isStitchItemSummary } from '@sharedlib/dtos/dto/StitchItemSummaryDto';

// RxJs
import { type Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// Components
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';

@Component({
  selector: 'app-side-bar-notebooks-tree',
  templateUrl: './side-bar-notebooks-tree.component.html',
  styleUrls: ['./side-bar-notebooks-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarNotebooksTreeComponent extends BaseSidebarContainersTreeComponent<
  Notebook,
  Note,
  NotebooksFilters,
  VirtualFolder
> {
  /**
   * The tenant this tree's notebooks belong to, when that is not the selected one (#1016).
   *
   * Both rail sections are on screen at once inside a team workspace, so the stored selection can
   * answer for only one of them: My Workspace's tree names the caller's member tenant while Team
   * Workspace's names the space. `undefined` means "use the stored selection", which is what the
   * personal rail and every other caller do.
   */
  @Input() public tenantId: string | null | undefined;

  selfDragDataTypes = [DragDataTypes.note, DragDataTypes.notebook];
  containerDragDataType = DragDataTypes.notebook;
  application = Application.notes;
  treeStateKey = StateKey.notesSidebarNotebooksTree;

  public dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.notebook &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type);

  constructor(
    private notebooksService: NotebooksService,
    private notesService: NotesService,
  ) {
    super(notebooksService);
  }

  protected override requestContext(): HttpContext | undefined {
    if (this.tenantId === undefined) {
      return undefined;
    }

    const context = mailboxTenantContext(this.tenantId);
    return this.tenantId ? context.set(OWNING_SPACE_ID, this.tenantId) : context;
  }

  /** Selects the tenant of the tree row before the notebook list reacts to the opened container. */
  public override openItem(item: Notebook): void {
    this.selectOwnNotebookTenant();
    super.openItem(item);
  }

  /**
   * Selects the tree tenant on the native click, before its synchronous container-selection write
   * can rebuild the notes list.
   *
   * `openItem` is deliberately still responsible for the same write: it is the normal open path,
   * but `appClick` defers it by 200 ms while the selectable directive acts immediately (#1016 r10).
   */
  protected override beforeContainerSelectionApplied(): void {
    this.selectOwnNotebookTenant();
  }

  /**
   * Drops a persisted notebook selection that is really gone (#2034: a notebook moved to Trash kept naming the Notes
   * header after a reload, because the rail reads only live notebooks and the base keeps the persisted name for an id it
   * has not loaded). Only the tree that owns Notes' current workspace drops — the personal rail's single tree, or the
   * team rail's tree whose tenant is Notes' effective one; the other mounted tree never holds the id by design (mail's
   * folder tree, #1065 ROW 6).
   *
   * The rows are read WITH the rail's filters (Flagged, Pinned, Stitch, the date window), so a live notebook those
   * filters hide is missing from them too (review-codex-2052 F1). A selection none of the rows holds is therefore
   * confirmed first by one owner-scoped read of exactly those ids with no rail filter, and dropped only if that read
   * answers none of them while the selection is still those ids.
   */
  protected override onItemsReceived(): void {
    if (this.tenantId === undefined || (this.tenantId ?? null) === this.notesTenantScope?.effectiveTenantId()) {
      this.confirmSelectionGone();
    }
  }

  private confirmSelectionGone(): void {
    const ids = this.selectedNotebookIds();
    const key = ids.join();
    // The base re-runs this hook on every search-state change: the same rows and selection are confirmed once.
    if (this.selectionCheck?.rows === this.items && this.selectionCheck.ids === key) {
      return;
    }
    // A newer rows load (or another selection) cancels a confirm in flight.
    this.selectionCheck?.read.unsubscribe();
    this.selectionCheck = undefined;
    if (ids.length === 0 || ids.some((id) => this.items.some((item) => item.id === id))) {
      return;
    }

    const requestContext = this.requestContext();
    const rows = this.items;
    const read = this.notebooksService
      .findAll(new NotebooksFilters({ ids, deleted: false }), requestContext ? { requestContext } : undefined)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((found) => {
        if (found.some((notebook) => ids.includes(notebook.id)) || this.selectedNotebookIds().join() !== key) {
          return;
        }
        this.dropForeignContainerSelection();
      });
    this.selectionCheck = { rows, ids: key, read };
  }

  private selectedNotebookIds(): string[] {
    return this.notesSearch.getStateSync()[this.application].containersIds;
  }

  /** Selects the tenant that owns this tree's notebook rows. */
  private selectOwnNotebookTenant(): void {
    this.stateService.setTeam(this.tenantId ? new Team({ id: this.tenantId }) : undefined);
  }

  /** Keeps cross-workspace dialogs honest while preserving every same-workspace action. */
  protected override getDndModalButtons(dragData: DragData, item: Stitch): AlertButtonConfig[] {
    const buttons = super.getDndModalButtons(dragData, item);

    if (!this.isCrossWorkspaceDrop(dragData)) {
      return buttons;
    }

    return buttons.map((button) => {
      if (dragData.type === DragDataTypes.notebook && button.title === 'Copy') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip: CROSS_WORKSPACE_DROP_TEXT.notebookCopyTooltip,
        };
      }

      if (button.title === 'Move') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        };
      }

      if (button.title === 'Stitch') {
        return {
          title: button.title,
          close: button.close,
          disabled: true,
          tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        };
      }

      return button;
    });
  }

  /** Owns every dialog this notes tree opens, while preserving the shared no-dialog paths. */
  public override dndDrop(dragData: DragData, item: Stitch): void {
    // Only a note (live Copy, r17) and a notebook (all three disabled, r18) get the honest dialog.
    // Round 18 dropped the type test, so a task, file or event dragged out of a note's linked-info list
    // into the other workspace also opened it — and kept the base's LIVE Copy, which calls
    // `handleDuplicate`, a note/notebook if-else-if with no `else`: a button that closes the dialog and
    // does nothing. Every other type stays on `super.dndDrop`, whose stitch path already refuses it.
    const crossWorkspace =
      this.isCrossWorkspaceDrop(dragData) &&
      (dragData.type === DragDataTypes.note || dragData.type === DragDataTypes.notebook);
    const opensSameWorkspaceDialog = dragData.type === DragDataTypes.note;

    if (!crossWorkspace && !opensSameWorkspaceDialog) {
      super.dndDrop(dragData, item);
      return;
    }

    this.crossWorkspaceAlertService.show({
      title: 'Copy, Move or Stitch?',
      body: 'What do we do with the dropped item?',
      note: crossWorkspace
        ? dragData.type === DragDataTypes.notebook
          ? CROSS_WORKSPACE_DROP_TEXT.notebookNote
          : CROSS_WORKSPACE_DROP_TEXT.note
        : undefined,
      rightButtons: this.getDndModalButtons(dragData, item),
    });
  }

  /**
   * Actions
   */

  /**
   * Both write paths carry {@link requestContext} (#1016 r2 row 6).
   *
   * Without it a drop onto a MEMBER notebook while the rail's Team Workspace section is selected wrote
   * with the space's tenant, because the stamp is read from the stored selection at dispatch — and both
   * trees are on screen at once, so the stored selection cannot answer for the tree that was dropped
   * on. The reads already named their tenant; these are the writes that did not.
   */
  public handleMove(dragData: DragData, item: Notebook): void {
    const message = 'Successfully moved to ' + item.title;
    const tenantItems = dragData.data.filter(isStitchItemSummary);

    if (dragData.type === DragDataTypes.note) {
      const ids = this.getIds(dragData.data);
      moveItemsIntoContainer(this.notesService, dragData.data, () =>
        this.notesService.bunchUpdate(
          { ids },
          { notebookId: item.id },
          { message, tenantItems, requestContext: this.requestContext() },
        ),
      );
    } else if (dragData.type === DragDataTypes.notebook) {
      this.notebooksService.bunchUpdate(
        { ids: this.getIds(dragData.data) },
        { parentId: item.id },
        { message, tenantItems, requestContext: this.requestContext() },
      );
    }
  }

  public handleDuplicate(dragData: DragData, notebook: Notebook): void {
    const tenantItems = dragData.data.filter(isStitchItemSummary);

    if (dragData.type === DragDataTypes.note) {
      this.notesService
        .duplicate(this.getIds(dragData.data), notebook.id, {
          tenantItems,
          requestContext: this.requestContext(),
        })
        .pipe(take(1))
        .subscribe();
    } else if (dragData.type === DragDataTypes.notebook) {
      this.notebooksService
        .duplicate(this.getIds(dragData.data), undefined, {
          tenantItems,
          requestContext: this.requestContext(),
        })
        .pipe(take(1))
        .subscribe();
    }
  }

  private readonly crossWorkspaceAlertService = inject(AlertService);
  private readonly notesTenantScope = inject(NotesTenantScopeService, { optional: true });
  private readonly notesSearch = inject(AdvancedSearchService);
  /** The rows and selection last confirmed, and that confirm read (cancelled by newer rows or another selection). */
  private selectionCheck?: { rows: Notebook[]; ids: string; read: Subscription };

  private isCrossWorkspaceDrop(dragData: DragData): boolean {
    const source = dragData.data[0];
    const sourceTenantId = getStringField(source, 'accountId');
    const targetTenantId = this.tenantId ?? this.spaceContextStore.contextId();

    return sourceTenantId !== undefined && targetTenantId !== undefined && sourceTenantId !== targetTenantId;
  }
}
